import React, { DOMAttributes, useLayoutEffect, useRef } from 'react';
import { InteractionBubbles } from '@hse-design/core';

import { useFactoryRef, useMixinClassRef } from '../../common/hooks';
import { usePropsRef } from '../../common/hooks/usePropsRef';

type DOMEvents<Base> = Pick<
  DOMAttributes<Base>,
  'onMouseDown' | 'onClick' | 'onKeyDown'
>;

export function useButtonBubbles<Base>(
  enabled: boolean,
  domEvents: DOMEvents<Base>
): [React.MutableRefObject<HTMLDivElement | null>, DOMEvents<Base>] {
  const bubbleRootRef = useRef<HTMLDivElement | null>(null);
  const bubbles = useMixinClassRef(InteractionBubbles.create);

  useLayoutEffect(() => {
    bubbles.update({ root: bubbleRootRef.current, enabled });
  });

  const propsRef = usePropsRef<DOMEvents<Base>>(domEvents);

  const funcsRef = useFactoryRef(() => ({
    onMouseDown: (event: React.MouseEvent<Base, MouseEvent>) => {
      bubbles.onMouseDown();
      propsRef.onMouseDown?.(event);
    },
    onKeyDown: (event: React.KeyboardEvent<Base>) => {
      bubbles.onKeyDown(event.nativeEvent);
      propsRef.onKeyDown?.(event);
    },
    onClick: (event: React.MouseEvent<Base>) => {
      bubbles.onClick();
      propsRef.onClick?.(event);
    }
  }));

  return [bubbleRootRef, funcsRef];
}
